import React, { useRef, RefObject, useState } from 'react';
import Link from 'next/link';
import PopupState from 'material-ui-popup-state';
import { bindHover, bindPopover, bindTrigger, PopupState as PopupStateType } from 'material-ui-popup-state/hooks';
import { useQuery } from '@apollo/client';

import { GetCollectionsNavigationDocument } from 'src/gql/graphql';
import type { Dispensaries, GetCollectionsNavigationQuery } from 'src/gql/graphql';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import usePaths from 'src/hooks/use-paths';
import useMenuFilterOptions from 'src/hooks/use-menu-filter-options';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import { useHeader } from 'src/dispensary/core-menu/components/header/use-header';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import useTranslation from 'src/hooks/use-translation';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';

import SvgChevronIcon from 'src/assets/chevron-icon';

import ROUTES from 'src/routes';
import { CartButton } from 'src/dispensary/core-menu/components/header/cart-button';
import { UserButton } from 'src/dispensary/core-menu/components/header/user-button';
import { Search } from 'src/dispensary/core-menu/components/header/search';
import { LoginNudge } from 'src/dispensary/core-menu/components/header/login-nudge';
import { StoreStatus } from 'src/dispensary/core-menu/components/header/store-status';
import { MenuType } from 'src/dispensary/core-menu/components/header/menu-type';
import { ConditionalLink } from 'src/utils/conditional-link';

import { StyledHoverPopover, TwoColumnPopoverNavLink } from 'src/dispensary/core-menu/components/header/header.styles';
import { getCustomCategoryLink } from 'src/dispensary/hooks/use-get-custom-category-link';
import {
  Divider,
  HeaderContainer,
  HeaderWrapper,
  InBetweenMq,
  Nav,
  NavButton,
  NavLink,
  ShopNav,
  ShopNavHeading,
  TwoColumnNav,
  ThreeColumnNav,
  UtilityNav,
} from './desktop-header.styles';

const PopupStateComponent = (PopupState as unknown) as (props: any) => JSX.Element;

type HeaderProps = {
  dispensary: Dispensaries;
  isAccountEnabled: boolean;
};

export const DesktopHeader = ({ dispensary, isAccountEnabled }: HeaderProps): JSX.Element => {
  const [userButtonRef, setUserButtonRef] = useState<RefObject<HTMLButtonElement> | null>(null);
  const utilityNavRef = useRef<HTMLDivElement>(null);
  const { href: baseHref } = usePaths();

  const { BRANDS, SPECIALS, COLLECTIONS } = ROUTES;
  const { t } = useTranslation();

  const { hasProductsOnSpecial } = useMenuFilterOptions({
    skipCurrentFilters: true,
  });
  const hideDiscount = useHideDiscountFromConsumer(dispensary);

  const currentCategory = useDispensaryCategory();
  const { categories } = useDispensaryCategories();

  const { trackEvent } = useHeader();

  const handleCategoryClick = (label: string, popupState: PopupStateType): void => {
    trackEvent(`categories > ${label}`);
    popupState.close();
  };

  const handleCollectionClick = (label: string, popupState: PopupStateType): void => {
    trackEvent(`collections > ${label}`);
    popupState.close();
  };

  const { data } = useQuery(GetCollectionsNavigationDocument, {
    skip: !dispensary,
    variables: {
      dispensaryId: dispensary.id,
      customCategory: false,
      enabled: true,
      activeOnly: true,
    },
  });

  const hideSalesLanguage = useHideSalesLanguage();
  const specialsText = hideSalesLanguage ? t('common.offer_plural', 'Offers') : t('common.special_plural', 'Specials');

  const collectionNavItems: NonNullable<GetCollectionsNavigationQuery['getCollectionsNavigation']> =
    data?.getCollectionsNavigation ?? [];
  const collections = useActiveScheduledItems({
    items: collectionNavItems,
    getSchedule: (collection) => collection.schedule,
    dispensaryTimezone: dispensary.timezone,
    enablePolling: true,
  });
  const totalNavItemCount = categories.length + collections.length;
  const useThreeColumns = totalNavItemCount > 18;
  const NavComponent = useThreeColumns ? ThreeColumnNav : TwoColumnNav;
  const columns = useThreeColumns ? 3 : 2;

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <Nav data-testid='navigation'>
          <Link href={baseHref}>
            <NavLink href={baseHref} onClick={() => trackEvent('Home')}>
              Home
            </NavLink>
          </Link>

          <PopupStateComponent variant='popover' popupId='categoriesPopover'>
            {(popupState) => (
              <>
                {/* Also bindTrigger for keyboard and touch device support */}
                <NavButton {...bindHover(popupState)} {...bindTrigger(popupState)} data-testid='nav-shop-button'>
                  Shop
                  <SvgChevronIcon height='7px' width='12px' />
                </NavButton>

                <StyledHoverPopover
                  {...bindPopover(popupState)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <ShopNav>
                    <>
                      <div>
                        <ShopNavHeading>Categories</ShopNavHeading>
                        <NavComponent
                          style={{ gridTemplateRows: `repeat(${Math.ceil(categories.length / columns)}, 1fr)` }}
                        >
                          {categories.map(({ key, label, slug, customCategory }) => {
                            const href = getCustomCategoryLink(baseHref, customCategory, slug, key);
                            return (
                              <li key={key}>
                                <ConditionalLink url={href}>
                                  <TwoColumnPopoverNavLink
                                    href={href}
                                    $isActive={currentCategory?.key === key}
                                    onClick={() => handleCategoryClick(label, popupState)}
                                  >
                                    {label}
                                  </TwoColumnPopoverNavLink>
                                </ConditionalLink>
                              </li>
                            );
                          })}
                        </NavComponent>
                      </div>

                      {collections.length > 0 && (
                        <div>
                          <ShopNavHeading>Collections</ShopNavHeading>
                          <NavComponent
                            style={{ gridTemplateRows: `repeat(${Math.ceil(collections.length / columns)}, 1fr)` }}
                          >
                            {collections.map(({ slug, navigationTitle, title }) => (
                              <li key={slug}>
                                <Link href={`${baseHref}${COLLECTIONS}${slug}`}>
                                  <TwoColumnPopoverNavLink
                                    href={`${baseHref}${COLLECTIONS}${slug}`}
                                    onClick={() => handleCollectionClick(navigationTitle ?? title, popupState)}
                                  >
                                    {navigationTitle ?? title}
                                  </TwoColumnPopoverNavLink>
                                </Link>
                              </li>
                            ))}
                          </NavComponent>
                        </div>
                      )}
                    </>
                  </ShopNav>
                </StyledHoverPopover>
              </>
            )}
          </PopupStateComponent>

          <Link href={`${baseHref}${BRANDS}`}>
            <NavLink href={`${baseHref}${BRANDS}`} onClick={() => trackEvent('Brands')}>
              Brands
            </NavLink>
          </Link>

          {hasProductsOnSpecial && !hideDiscount && (
            <Link href={`${baseHref}${SPECIALS}`}>
              <NavLink href={`${baseHref}${SPECIALS}`} onClick={() => trackEvent('Specials')}>
                {specialsText}
              </NavLink>
            </Link>
          )}

          <Search />
        </Nav>

        <UtilityNav ref={utilityNavRef}>
          <InBetweenMq>
            <StoreStatus />

            <Divider />

            <MenuType />

            {isAccountEnabled && <Divider />}
          </InBetweenMq>

          {isAccountEnabled && <UserButton onRef={setUserButtonRef} />}

          <CartButton />

          {isAccountEnabled && <LoginNudge anchorEl={userButtonRef} containerRef={utilityNavRef} />}
        </UtilityNav>
      </HeaderContainer>
    </HeaderWrapper>
  );
};
